import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone, ExternalLink, TestTube, Server } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { printRawBTTestPage, isAndroidDevice } from "@/lib/rawbt-print";
import { invalidatePrintModeCache, type PrintMode } from "@/lib/print-dispatcher";

export function PrintModeSelector() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [mode, setMode] = useState<PrintMode>("qz_tray");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeRestaurant) {
      setMode((activeRestaurant as any).print_mode || "qz_tray");
    }
  }, [activeRestaurant]);

  const handleChange = async (value: PrintMode) => {
    if (!activeRestaurant) return;
    
    setMode(value);
    setSaving(true);

    try {
      const { error } = await supabase
        .from("restaurants")
        .update({ print_mode: value })
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      invalidatePrintModeCache(activeRestaurant.id);
      await refetch();
      toast.success(`Modo de impressão alterado para ${value === "rawbt" ? "RawBT (Android)" : value === "mixed" ? "Misto (Windows + Android)" : value === "local_service" ? "Servidor Local" : "QZ Tray (Windows)"}`);
    } catch (err) {
      console.error("Error updating print mode:", err);
      toast.error("Erro ao salvar modo de impressão");
      setMode((activeRestaurant as any).print_mode || "qz_tray");
    } finally {
      setSaving(false);
    }
  };

  const handleTestRawBT = () => {
    const success = printRawBTTestPage();
    if (success) {
      toast.info("Impressão de teste enviada ao RawBT");
    } else {
      toast.error("Falha ao enviar teste. Verifique se o RawBT está instalado.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Modo de Impressão</CardTitle>
        <CardDescription>
          Escolha como este estabelecimento envia impressões
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={mode} onValueChange={(v) => handleChange(v as PrintMode)} disabled={saving}>
          <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="qz_tray" id="mode-qz" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="mode-qz" className="flex items-center gap-2 cursor-pointer font-medium">
                <Monitor className="h-4 w-4" />
                QZ Tray (Windows/Mac)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Requer PC com QZ Tray instalado. Suporta fila de impressão, retry automático e múltiplas impressoras.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="mixed" id="mode-mixed" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="mode-mixed" className="flex items-center gap-2 cursor-pointer font-medium">
                <Monitor className="h-4 w-4" />
                <Smartphone className="h-4 w-4 -ml-1" />
                Misto (Windows + Android)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                PC imprime via QZ Tray e serve como servidor de fila. Terminais Android imprimem direto via RawBT, sem depender do PC.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="rawbt" id="mode-rawbt" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="mode-rawbt" className="flex items-center gap-2 cursor-pointer font-medium">
                <Smartphone className="h-4 w-4" />
                RawBT (Android)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Para terminais Android (Sunmi, etc.) sem depender de PC. Requer app RawBT instalado no dispositivo.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="local_service" id="mode-local" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="mode-local" className="flex items-center gap-2 cursor-pointer font-medium">
                <Server className="h-4 w-4" />
                Servidor Local (AltusFood Local Server)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                O servidor local da loja imprime direto pela rede, sem depender do navegador/QZ Tray.
                Neste modo o frontend NÃO envia impressões para a nuvem (evita duplicação).
              </p>
            </div>
          </div>
        </RadioGroup>

        {(mode === "rawbt" || mode === "mixed") && (
          <div className="space-y-3 p-3 bg-muted/50 rounded-lg border">
            <h4 className="font-medium text-sm">Como configurar o RawBT:</h4>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Instale o app <strong>RawBT</strong> da Play Store</li>
              <li>Abra o RawBT e configure a impressora (USB ou Rede)</li>
              <li>Para impressora de rede, insira o IP e porta (ex: 192.168.1.100:9100)</li>
              <li>Faça um teste clicando no botão abaixo</li>
            </ol>
            
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestRawBT}
              >
                <TestTube className="mr-2 h-4 w-4" />
                Testar Impressão
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a
                  href="https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Baixar RawBT
                </a>
              </Button>
            </div>

            <div className="text-xs text-muted-foreground border-t pt-2 mt-2 space-y-1">
              <p>⚠️ O app RawBT abrirá brevemente a cada impressão</p>
              <p>⚠️ Impressões são enviadas para a impressora padrão do RawBT</p>
              {!isAndroidDevice() && (
                <p className="text-destructive font-medium">⚠️ Este dispositivo não parece ser Android. RawBT só funciona em Android.</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
